import { useEffect, useState } from 'react'

import Banner, { BannerType, BannerVariaion } from '@cox/core-ui8/dist/Banner'
import { FormOptionsTypes } from '@cox/core-ui8/dist/Form'
import FormDropdown from '@cox/core-ui8/dist/FormDropdown'
import SectionHeader from '@cox/core-ui8/dist/SectionHeader'
import { Spinner } from '@cox/core-ui8/dist/Spinner'
import useLobExperience from '@cox/core-ui8/dist/useLobExperience'
import axios from 'axios'

import ChannelLineupSubHeader from './ChannelLineupSubHeader'
import ChannelLineupTable from './ChannelLineupTable'
import { ChannelLineupTableStatus } from './ChannelLineupTable/types'
import { ChannelLineupLayout, ReactChannelLineupProps } from './types'
import { WEB_APIS } from '../../constants'
import { createlineupParams, getDropdownOptions } from '../../utils/channel-lineup-util'
import { getSpacing } from '../../utils/spacing-util'
import { ScreenSizes } from '../../utils/types'
import ReactText from '../ReactText'
import IsInEditorText from '../shared/IsInEditorText'
import './ReactChannelLineup.scss'

const ReactChannelLineup = (props: ReactChannelLineupProps) => {
  const {
    isInEditor = false,
    channelLineupLayout = window.outerWidth < ScreenSizes.MD ? ChannelLineupLayout.MOBILE : ChannelLineupLayout.DESKTOP,
    heading = '',
    disclaimer = '',
    pkg = '',
    sortChannelsOptionsTitle = '',
    errorMsg,
    errorMsgIcon,
    errorMsgIconAlt,
    searchNoMatchMsg,
    searchNoMatchMsgIcon,
    searchNoMatchMsgIconAlt,
    top = false,
    right = false,
    bottom = false,
    left = false,
    lob = ''
  } = props

  const { isBusiness } = useLobExperience()

  const [layout, setLayout] = useState(channelLineupLayout)

  const isModalView = channelLineupLayout === ChannelLineupLayout.MODAL
  const modalValue = isModalView ? ChannelLineupLayout.MODAL : ''
  const lineupResultsCount = isModalView ? 80 : 40

  const CHANNEL_LINEUP_API = isBusiness
    ? `${WEB_APIS.CHANNEL_LINEUP_CB}${modalValue}`
    : `${WEB_APIS.CHANNEL_LINEUP}${modalValue}`

  const [sortOption, setSortOption] = useState('number')

  /** Initial Lineup Parameters */
  const lineupParameters: any = {
    ...(isModalView ? { pkg: pkg } : {}),
    layout: isModalView ? ChannelLineupLayout.MODAL : channelLineupLayout,
    ...(channelLineupLayout !== ChannelLineupLayout.MOBILE
      ? {
          'sort-by-select': sortOption
        }
      : {}),
    'filter-by-select': 'en',
    status: ChannelLineupTableStatus.INIT,
    start: 0,
    end: lineupResultsCount
  }

  const [lineupParams, setLineupParams] = useState(lineupParameters)
  const [searchParamsQueryString, setSearchParamsQueryString] = useState('')
  const [lineupAPIwParams, setLineupAPIwParams] = useState(
    `${CHANNEL_LINEUP_API}?${createlineupParams(lineupParams)}${
      searchParamsQueryString == '' ? '' : `&${searchParamsQueryString}`
    }`
  )

  const [lineupData, setLineupData] = useState({ loading: true } as any)

  useEffect(() => {
    window.addEventListener('resize', updateLayout)

    return () => {
      window.removeEventListener('resize', updateLayout)
    }
  }, [])

  const updateLayout = () => {
    setLayout(
      window.outerWidth < ScreenSizes.MD
        ? ChannelLineupLayout.MOBILE
        : isModalView
        ? ChannelLineupLayout.MODAL
        : ChannelLineupLayout.DESKTOP
    )
  }

  /**
   * Updates the lineup parameters.
   */
  const updateLineupParams = (mode?: any, event?: any, queryString?: string) => {
    const updatedParams = {
      ...lineupParameters,
      layout,
      ...(mode === 'sort' && { 'sort-by-select': event.value }),
      ...(mode === 'filter' && { start: 0, end: lineupResultsCount, status: ChannelLineupTableStatus.INIT })
    }
    setLineupParams(updatedParams)

    queryString && setSearchParamsQueryString(queryString)

    switch (mode) {
      case 'reset':
        setLineupAPIwParams(`${CHANNEL_LINEUP_API}?${createlineupParams(updatedParams)}`)
        break
      case 'filter':
        setLineupAPIwParams(`${CHANNEL_LINEUP_API}?${createlineupParams(updatedParams)}&${queryString}`)
        break
      case 'sort':
        setSortOption(event.value)
        setLineupAPIwParams(
          `${CHANNEL_LINEUP_API}?${createlineupParams(updatedParams)}${
            searchParamsQueryString == '' ? '' : `&${searchParamsQueryString}`
          }`
        )
        break
      default:
        setLineupAPIwParams(
          `${CHANNEL_LINEUP_API}?${createlineupParams(updatedParams)}${
            searchParamsQueryString == '' ? '' : `&${searchParamsQueryString}`
          }`
        )
    }
  }

  useEffect(() => {
    setLineupData({ loading: true })
    updateLineupParams()
  }, [layout])

  const getChannelLineupData = async () => {
    try {
      const { data } = await axios.get(lineupAPIwParams)
      setLineupData({ ...data, loading: false })
    } catch (error: any) {
      setLineupData({ loading: false, success: false })
    }
  }

  useEffect(() => {
    getChannelLineupData()
  }, [lineupAPIwParams])

  const loadMoreData = () => {
    const updateLineupParameters = {
      ...lineupParams,
      status: ChannelLineupTableStatus.APPEND,
      start: lineupParams.end + 1,
      end: lineupParams.end + lineupResultsCount
    }
    setLineupParams(updateLineupParameters)
    setLineupAPIwParams(
      `${CHANNEL_LINEUP_API}?${createlineupParams(updateLineupParameters)}${
        searchParamsQueryString == '' ? '' : `&${searchParamsQueryString}`
      }`
    )
  }

  return (
    <>
      {IsInEditorText(isInEditor, 'React Channel Lineup')}
      <div
        className={`react-channel-lineup__container ${getSpacing(top, right, bottom, left)} ${lob}`}
        data-testid='react-channel-lineup__container'
      >
        {heading && (
          <SectionHeader
            title={{
              value: heading,
              isRTE: true,
              className: 'react-channel-lineup__heading'
            }}
          />
        )}
        {channelLineupLayout !== ChannelLineupLayout.MODAL && lineupData?.loading == false ? (
          <ChannelLineupSubHeader
            {...props}
            updateLineupParams={updateLineupParams}
            lineupData={lineupData}
            lineupParams={lineupParams}
            setSearchParamsQueryString={setSearchParamsQueryString}
          />
        ) : (
          <div className='react-channel-lineup__details-sub-header'>
            <div className='react-channel-lineup__details'>
              <h2>{lineupData?.packageName}</h2>
              <div>{lineupData?.packageDesc}</div>
            </div>
            {!lineupData?.loading && (
              <div className='react-channel-lineup__sort-dropdown'>
                <FormDropdown
                  id={'sort-channels-dropdown'}
                  title={sortChannelsOptionsTitle}
                  name='sort-channels-dropdown'
                  type={FormOptionsTypes.dropdown}
                  searchEnabled={false}
                  items={getDropdownOptions(lineupData?.sortSelect)}
                  onChange={(evt) => updateLineupParams('sort', evt)}
                />
              </div>
            )}
          </div>
        )}

        {lineupData?.loading ? (
          <div className='react-channel-lineup__spinner'>
            <Spinner size='xl' />
          </div>
        ) : lineupData?.success || lineupData?.tableHeader?.length ? (
          lineupData?.totalResults > 0 ? (
            <>
              <ChannelLineupTable
                {...props}
                lineupParams={lineupParams}
                lineupData={lineupData}
                loadMoreData={loadMoreData}
              />
            </>
          ) : (
            <Banner
              bannerType={BannerType.DYNAMIC}
              message={searchNoMatchMsg}
              variation={BannerVariaion.ERROR}
              iconPath={searchNoMatchMsgIcon}
              altIcon={searchNoMatchMsgIconAlt}
            />
          )
        ) : (
          <Banner
            bannerType={BannerType.DYNAMIC}
            message={errorMsg}
            variation={BannerVariaion.ERROR}
            iconPath={errorMsgIcon}
            altIcon={errorMsgIconAlt}
          />
        )}
        {disclaimer && <ReactText text={disclaimer} className='react-channel-lineup__disclaimer' />}
      </div>
    </>
  )
}

export default ReactChannelLineup
